import { createBrowserRouter, Navigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";

// Layouts
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { AuthLayout } from "@/layouts/AuthLayout";

// Auth pages
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { ForgotPasswordPage } from "@/features/auth/pages/ForgotPasswordPage";
import { ForcePasswordChangePage } from "@/features/auth/pages/ForcePasswordChangePage";

// Dashboard
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage";

// Customers
import { CustomersListPage } from "@/features/customers/pages/CustomersListPage";
import { CustomerDetailPage } from "@/features/customers/pages/CustomerDetailPage";

// Drivers
import { DriversListPage } from "@/features/drivers/pages/DriversListPage";
import { KYCPendingPage } from "@/features/drivers/pages/KYCPendingPage";
import { KYCDetailPage } from "@/features/drivers/pages/KYCDetailPage";
import { DriverDetailPage } from "@/features/drivers/pages/DriverDetailPage";

// Vehicles
import { VehiclesListPage } from "@/features/vehicles/pages/VehiclesListPage";
import { VehiclePendingPage } from "@/features/vehicles/pages/VehiclePendingPage";
import { VehicleDetailPage } from "@/features/vehicles/pages/VehicleDetailPage";

// Bookings
import { BookingsListPage } from "@/features/bookings/pages/BookingsListPage";
import { BookingDetailPage } from "@/features/bookings/pages/BookingDetailPage";
import { LiveTrackingPage } from "@/features/bookings/pages/LiveTrackingPage";

// Pricing
import { FareRulesPage } from "@/features/pricing/pages/FareRulesPage";
import { CommissionPage } from "@/features/pricing/pages/CommissionPage";

// Payments
import { PaymentsListPage } from "@/features/payments/pages/PaymentsListPage";
import { PayoutsPage } from "@/features/payments/pages/PayoutsPage";
import { DriverEarningsPage } from "@/features/payments/pages/DriverEarningsPage";

// Support
import { SupportTicketsPage } from "@/features/support/pages/SupportTicketsPage";

// Reports
import { ReportsPage } from "@/features/reports/pages/ReportsPage";
import { GstReportPage } from "@/features/reports/pages/GstReportPage";

// Zipto Shield
import { ZiptoShieldPage } from "@/features/shield/pages/ZiptoShieldPage";

// Fraud
import { FraudReportsPage } from "@/features/fraud/pages/FraudReportsPage";
import { UnblockRequestsPage } from "@/features/fraud/pages/UnblockRequestsPage";
import { FraudIntelligencePage } from "@/features/fraud/pages/FraudIntelligencePage";

// Notifications
import { PushNotificationsPage } from "@/features/notifications/pages/PushNotificationsPage";

// Settings
import { SettingsPage } from "@/features/settings/pages/SettingsPage";

// Coupons
import { CouponsPage } from "@/features/coupons/pages/CouponsPage";

// Referrals
import { ReferralsPage } from "@/features/referrals/pages/ReferralsPage";

export const router = createBrowserRouter([
  // Standalone forced password change (authenticated, no nav chrome)
  {
    path: ROUTES.CHANGE_PASSWORD,
    element: <ForcePasswordChangePage />,
  },

  // Auth routes
  {
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
    ],
  },

  // Protected dashboard routes
  {
    element: <DashboardLayout />,
    children: [
      // Dashboard
      {
        path: ROUTES.DASHBOARD,
        element: <DashboardPage />,
      },

      // Customers
      {
        path: ROUTES.CUSTOMERS,
        element: <CustomersListPage />,
      },
      {
        path: ROUTES.CUSTOMER_DETAIL,
        element: <CustomerDetailPage />,
      },

      // Drivers
      {
        path: ROUTES.DRIVERS,
        element: <DriversListPage />,
      },
      {
        path: ROUTES.KYC_PENDING,
        element: <KYCPendingPage />,
      },
      {
        path: ROUTES.KYC_DETAIL,
        element: <KYCDetailPage />,
      },
      {
        path: ROUTES.DRIVER_DETAIL,
        element: <DriverDetailPage />,
      },

      // Vehicles
      {
        path: ROUTES.VEHICLES,
        element: <VehiclesListPage />,
      },
      {
        path: ROUTES.VEHICLE_PENDING,
        element: <VehiclePendingPage />,
      },
      {
        path: ROUTES.VEHICLE_DETAIL,
        element: <VehicleDetailPage />,
      },

      // Bookings
      {
        path: ROUTES.BOOKINGS,
        element: <BookingsListPage />,
      },
      {
        path: ROUTES.BOOKING_DETAIL,
        element: <BookingDetailPage />,
      },
      {
        path: ROUTES.LIVE_TRACKING,
        element: <LiveTrackingPage />,
      },

      // Pricing
      {
        path: ROUTES.PRICING,
        element: <Navigate to={ROUTES.FARE_RULES} replace />,
      },
      {
        path: ROUTES.FARE_RULES,
        element: <FareRulesPage />,
      },
      {
        path: ROUTES.COMMISSION,
        element: <CommissionPage />,
      },

      // Payments
      {
        path: ROUTES.PAYMENTS,
        element: <PaymentsListPage />,
      },
      {
        path: ROUTES.PAYOUTS,
        element: <PayoutsPage />,
      },
      {
        path: ROUTES.DRIVER_EARNINGS,
        element: <DriverEarningsPage />,
      },

      // Support
      {
        path: ROUTES.SUPPORT,
        element: <SupportTicketsPage />,
      },

      // Reports
      {
        path: ROUTES.REPORTS,
        element: <ReportsPage />,
      },
      {
        path: ROUTES.BOOKING_REPORTS,
        element: <ReportsPage />,
      },
      {
        path: ROUTES.REVENUE_REPORTS,
        element: <ReportsPage />,
      },
      {
        path: ROUTES.DRIVER_REPORTS,
        element: <ReportsPage />,
      },
      {
        path: ROUTES.GST_REPORT,
        element: <GstReportPage />,
      },

      // Fraud & Safety
      {
        path: ROUTES.FRAUD,
        element: <FraudReportsPage />,
      },
      {
        path: ROUTES.FRAUD_REPORTS,
        element: <FraudReportsPage />,
      },
      {
        path: ROUTES.UNBLOCK_REQUESTS,
        element: <UnblockRequestsPage />,
      },
      {
        path: ROUTES.FRAUD_INTELLIGENCE,
        element: <FraudIntelligencePage />,
      },

      // Zipto Shield
      {
        path: ROUTES.ZIPTO_SHIELD,
        element: <ZiptoShieldPage />,
      },

      // Push Notifications
      {
        path: ROUTES.PUSH_NOTIFICATIONS,
        element: <PushNotificationsPage />,
      },

      // Coupons
      {
        path: ROUTES.COUPONS,
        element: <CouponsPage />,
      },

      // Referrals
      {
        path: ROUTES.REFERRALS,
        element: <ReferralsPage />,
      },

      // Settings & Profile
      {
        path: ROUTES.SETTINGS,
        element: <SettingsPage />,
      },
      {
        path: ROUTES.PROFILE,
        element: <SettingsPage />,
      },

      // Catch all - redirect to dashboard
      {
        path: "*",
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
    ],
  },
]);
